/**
 * dashboard 域 Zod 共享契约 — ADM-DASH-S001~S010
 *
 * 契约源（提案）：dev-docs PR #68 `docs/proposals/2026-05-31-admin-gap-domains-contract.md`
 * §「数据总览 Dashboard（ADM-DASH）」§2.2 建议端点 + `data` 响应结构。
 * 待 PR #68 merge 进 `00-详细需求/30-运营后台需求文档.md` §2 + `03-数据字典/05-运营总览聚合.md` 后对齐。
 *
 * 口径分层（见提案 §2.1 MVP? 列）：
 * - ✅ 交易事实表直查可算：kpi（除 dau）/ revenue_trend / revenue_by_app /
 *   user_trend.paying_users_series / top_books（四项计数全部）/ alerts
 * - 🔸 行为埋点 sink（`analytics_events_raw`，Neon 直查）依赖，范围内无就绪 App → 字段返回
 *   `status: 'PENDING_INSTRUMENTATION'` 占位（HTTP 200，envelope code=OK），**禁止返回 mock 假数据**：
 *   dau / retention / funnel / user_trend.dau_series；就绪判据与口径见 `behavioral_coverage`
 *   （`top_books.readers` **不在此列**：取 `reading_progress` 业务事实、恒为 integer）
 *
 * 金额：bigint minor units（分），API 序列化为 string；币种字段独立（默认 EUR）。
 */

import { z } from 'zod';
import type { FinancialCoverage } from './financial-coverage';

/** 时间窗闭集（range）；当期 = [now-range, now)，环比上一周期 = [now-2*range, now-range) */
export const DASHBOARD_RANGES = ['24h', '7d', '30d', '90d'] as const;
export type DashboardRange = (typeof DASHBOARD_RANGES)[number];

/** 行为埋点未就绪的占位标记（非 error，HTTP 200） */
export const PENDING_INSTRUMENTATION = 'PENDING_INSTRUMENTATION' as const;
export type PendingInstrumentation = { status: typeof PENDING_INSTRUMENTATION };

/**
 * GET /api/v1/dashboard/overview query 校验：
 * - app_scope：缺省 / 'all' = 全部 App 汇总；否则必须是合法 UUID（防注入 metadata->>'app_id' 比较）
 * - range：闭集，缺省 30d
 */
export const DashboardOverviewQuerySchema = z.object({
  app_scope: z
    .union([z.literal('all'), z.string().uuid()])
    .optional()
    .default('all'),
  range: z.enum(DASHBOARD_RANGES).optional().default('30d'),
});
export type DashboardOverviewQuery = z.infer<typeof DashboardOverviewQuerySchema>;

export type DashboardScope = 'all' | 'app';

/** 单点时间序列（逐日；24h 窗逐小时）*/
export type DashboardSeriesPoint = { date: string; value: number };

/** KPI 卡（除被动退款率外通用：当期值 + 环比百分比）*/
export type DashboardKpiMetric = { value: string | number | null; delta_pct: number | null };

/**
 * 被动退款率卡（百分点 delta）。
 * 本轮仅 value + delta_pp（提案 §2.1.1 / S001，张培 09:11 review）；threshold / 越界 status
 * 随 ADM-FIN-S008 退款告警后续启用再补 —— S008 本轮移出 MVP，Dashboard 不展示阈值 / 越界状态。
 */
export type DashboardRefundRateKpi = {
  value: number | null;
  delta_pp: number | null;
};

export type DashboardKpi = {
  /** ✅ 当期净充值收入 minor units（Σ 充值 − Σ 退款）*/
  total_revenue: DashboardKpiMetric;
  /** ✅ 当期去重付费用户数 */
  paying_users: DashboardKpiMetric;
  /** 🔸 行为埋点：日活去重用户数 */
  dau: DashboardKpiMetric | PendingInstrumentation;
  /** ✅ 派生 total_revenue / paying_users（minor units string，付费用户=0 时 value=null）*/
  arppu: { value: string | null; currency: string; delta_pct: number | null };
  /** ✅ 被动退款金额 / 总充值金额 */
  passive_refund_rate: DashboardRefundRateKpi;
};

/**
 * 收入趋势（按支付渠道拆分，逐日；金额 minor units string）。
 * 渠道闭集 = IAP / STRIPE（提案 §2.1.2 / S002，对齐 ADM-APP-S009，张培裁决不保留 PayPal）。
 */
export type DashboardRevenueTrendPoint = {
  /** range=24h 逐小时键 `YYYY-MM-DDTHH:00`(UTC 整点)，其余逐日 `YYYY-MM-DD`（契约 A0 §1.2 + 05 §2.2）*/
  date: string;
  /** 币种（Adapty USD 分析口径；契约 A0 §1.2 points 示例逐项含 currency）*/
  currency: string;
  iap: string;
  stripe: string;
};
export type DashboardRevenueTrend = {
  channels: Array<'IAP' | 'STRIPE'>;
  points: DashboardRevenueTrendPoint[];
};

export type DashboardUserTrend = {
  /** ✅ 逐日去重付费用户数 */
  paying_users_series: DashboardSeriesPoint[];
  /** 🔸 行为埋点：逐日 DAU */
  dau_series: DashboardSeriesPoint[] | PendingInstrumentation;
};

/** App 营收占比（仅 scope=all）*/
export type DashboardRevenueByApp = {
  app_id: string;
  app_name: string;
  revenue: string;
  /** 币种（USD 运营口径；契约 A0 §1.2 revenue_by_app 示例逐项含 currency）*/
  currency: string;
  share_pct: number;
};

/**
 * 留存矩阵 🔸（无就绪 App → PENDING_INSTRUMENTATION）。cohorts 按 cohort_start（ISO 周一，UTC）升序；
 * days[n] = Dn 留存百分比（0–100，1 位小数，D0 恒 100）；未完整过去的 Dn 不输出 → 后期 cohort 的
 * days 较短（三角矩阵），长度 ≤ day_span。
 */
export type DashboardRetention = {
  step: 'WEEK';
  day_span: number;
  cohorts: Array<{ cohort_start: string; size: number; days: number[] }>;
};

/**
 * 转化漏斗六级闭集（dev-docs 05 §2.3）：step 为稳定枚举，event 为 sink 事件名（screen_view 两级
 * 由 screen_name 区分）。顺序即漏斗级次。
 */
export const DASHBOARD_FUNNEL_STEPS = [
  { step: 'APP_OPEN', event: 'app_open' },
  { step: 'HOME_VIEW', event: 'screen_view' },
  { step: 'NOVEL_VIEW', event: 'screen_view' },
  { step: 'READING_START', event: 'chapter_view' },
  { step: 'PAID_UNLOCK', event: 'chapter_unlock_success' },
  { step: 'PURCHASE', event: 'store_purchase_success' },
] as const;
export type DashboardFunnelStepKey = (typeof DASHBOARD_FUNNEL_STEPS)[number]['step'];

/** 转化漏斗单级 🔸（users = 完成本级及之前全部级次的去重用户）*/
export type DashboardFunnelStep = {
  step: DashboardFunnelStepKey;
  event: string;
  users: number;
};

/**
 * 行为指标覆盖：范围内各 App 的 sink 就绪划分。all 口径只聚合 ready_app_ids，
 * missing_app_ids 的 App 不计入行为指标（不补 0）；单 App 口径二者恰有一个含该 App。
 */
export type DashboardBehavioralCoverage = {
  ready_app_ids: string[];
  missing_app_ids: string[];
};

/**
 * 实时在线 🔸 — 🚫 ADM-DASH-S007 本轮不实现（张培裁决 2026-05-31）。
 * 需近实时埋点聚合管线、无稳定数据源，本轮移除：overview 响应不含 realtime 块、无 /dashboard/realtime 端点。
 * 类型 export 保留为后续建设近实时埋点链路时的蓝本（DashboardOverviewResponse 已不再引用）。
 */
export type DashboardRealtime = {
  online_now: number;
  online_series: Array<{ minute_offset: number; online: number }>;
  breakdown: { reading: number; charging: number; subscribing: number };
};

/** 热门书籍 TOP N（ADM-DASH-S008）——按窗口内活跃阅读 UV 排序；四项计数全部接真、按书组归并 */
export type DashboardTopBook = {
  novel_id: string;
  /** 书名（novels.title，per-locale 行单字符串；dev-docs #118）*/
  title: string;
  author: string | null;
  /** 主分类（novels.category_id 必填 → categories.name_i18n，前端按 admin 语言取；恒有，dev-docs #118）*/
  category: { id: string; name_i18n: Record<string, string> };
  /** 本 locale 封面 CDN URL（novels.cover_url 必填，恒有；dev-docs #118）*/
  cover_url: string;
  /**
   * ✅ 本次查询 `range` 窗口内的**活跃**阅读 UV：`reading_progress.last_read_at ∈ [now-range, now)`
   * 后按书组去重 `user_id`，恒为 integer（**不**依赖埋点、不受数据分析授权影响）。
   * ⚠ 与 `A6-书籍域.md` §19 `reader_user_count`（全时累计、读 `novel_groups.real_read_count`）**不同口径**，
   * 两者不可互相引用或比对。
   */
  readers: number;
  payers: number;
  paid_chapters: number;
  /** 解锁消费 Coin minor-units（章节解锁口径，非法币充值额）*/
  revenue: string;
};

/** 风险与告警聚合（只读视图）*/
export type DashboardAlerts = {
  pending_count: number;
  items: Array<{
    type: string;
    level: 'INFO' | 'WARN' | 'CRITICAL';
    title: string;
    detail: string;
    occurred_at: string;
    ref: { domain: string; resource_id: string };
  }>;
};

/**
 * GET /api/v1/dashboard/overview 响应 data。
 * 行为类字段（dau / retention / funnel / dau_series）在范围内无就绪 App 时为
 * PENDING_INSTRUMENTATION 占位（见文件头口径）。
 * 实时在线（S007）本轮不实现 → 响应不含 realtime 块（提案 §2.1.6 / §2.2 / JSON）。
 */
export type DashboardOverviewResponse = {
  financial_coverage: FinancialCoverage;
  app_scope: 'all' | string;
  range: DashboardRange;
  scope: DashboardScope;
  period: { start: string; end: string };
  currency: string;
  generated_at: string;
  /** 各源新鲜度标注（txn=交易事实表强一致；behavioral=行为 sink 直查，无就绪 App 时为占位）*/
  data_freshness: { txn: 'realtime'; behavioral: 'realtime' | typeof PENDING_INSTRUMENTATION };
  /** 行为指标 sink 就绪覆盖（dau / dau_series / retention / funnel 只聚合 ready_app_ids）*/
  behavioral_coverage: DashboardBehavioralCoverage;
  kpi: DashboardKpi;
  revenue_trend: DashboardRevenueTrend;
  user_trend: DashboardUserTrend;
  /** 仅 scope=all 返回；scope=app 时为空数组 */
  revenue_by_app: DashboardRevenueByApp[];
  retention: DashboardRetention | PendingInstrumentation;
  funnel: DashboardFunnelStep[] | PendingInstrumentation;
  funnel_overall_rate: number | null;
  top_books: DashboardTopBook[];
  alerts: DashboardAlerts;
};

/**
 * ADM-DASH-S010 报表导出请求 — 复用 overview 的 app_scope / range 口径（导出即该口径的 overview 快照）。
 */
export const DashboardExportRequestSchema = z.object({
  app_scope: z
    .union([z.literal('all'), z.string().uuid()])
    .optional()
    .default('all'),
  range: z.enum(DASHBOARD_RANGES).optional().default('30d'),
});
export type DashboardExportRequest = z.infer<typeof DashboardExportRequestSchema>;

/**
 * ADM-DASH-S010 导出任务记录（admin 独有表 dashboard_exports）。
 * 同步生成：提交即生成 CSV → 写 R2 → 落 COMPLETED 记录（失败落 FAILED + error）。
 * 下载走**认证端点** download_path（admin RBAC 代理 R2 流），非 bearer 签名 URL（报表含财务数据）。
 * CSV 仅含交易事实表可算的 ✅ 指标（KPI 除 dau / revenue_trend / revenue_by_app / top_books 的 payers/revenue）；
 * 行为埋点未就绪的 🔸 指标（dau/retention/funnel）**剔除不导**（不造假数据，与 overview 口径一致）；
 * `top_books.readers` 取 `reading_progress` 业务事实、恒有值，属 ✅ 直接导。
 */
export type DashboardExportRecord = {
  id: string;
  app_scope: 'all' | string;
  range: DashboardRange;
  status: 'COMPLETED' | 'FAILED';
  format: 'CSV';
  /** CSV 数据行数（不含 section header / 空行）；FAILED 时 null */
  row_count: number | null;
  /** FAILED 时的脱敏原因；COMPLETED 时 null */
  error: string | null;
  created_by: string;
  created_at: string;
  /** 认证下载路径（GET /api/v1/dashboard/exports/:id/download）；COMPLETED 才可下载 */
  download_path: string | null;
};

export type DashboardExportListResponse = { items: DashboardExportRecord[] };

/** 类型守卫：判断某字段是否为埋点占位 */
export function isPendingInstrumentation(v: unknown): v is PendingInstrumentation {
  return (
    typeof v === 'object' &&
    v !== null &&
    'status' in v &&
    (v as { status?: unknown }).status === PENDING_INSTRUMENTATION
  );
}
